import { Action } from "@/api-schema-enums";
import { Can } from "@/casl-provider";
import {
  useDepartmentByUuidQuery,
  type DepartmentByUuidParameters,
  type DepartmentByUuidResponse,
} from "@/common/api/department/use-department-by-uuid.query";
import { UserTable } from "@/components/data-table/predefined/user-table";
import { DetailHeader } from "@/components/detail-page/detail-header";
import { DetailPageLayout } from "@/components/detail-page/detail-page-layout";
import { DetailPageSkeleton } from "@/components/detail-page/detail-page-skeleton";
import { DetailTabs } from "@/components/detail-page/detail-tabs";
import { SystemIdTab } from "@/components/detail-page/tabs/system-id-tab";
import type { DetailAction, DetailTab } from "@/components/detail-page/types";
import { CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { BuildingIcon, Fingerprint, PencilIcon, UsersIcon } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";

const DEPARTMENT_DETAIL_TRACKING_NAME = "department";

export function DepartmentDetailView(): ReactElement {
  return (
    <Can I="read" a="Department">
      <DepartmentDetailQuery />
    </Can>
  );
}

function DepartmentDetailQuery(): ReactElement {
  const { t } = useTranslation(["masterdata"]);
  const params = useParams<{ uuid: string }>();
  const uuid = params.uuid ?? "";
  const departmentQuery = useDepartmentByUuidQuery(uuid);

  if (departmentQuery.isPending) {
    return <DetailPageSkeleton />;
  }

  return (
    <UuidExistenceWrapper<DepartmentByUuidParameters, DepartmentByUuidResponse>
      uuid={uuid}
      query={useDepartmentByUuidQuery}
      queryResult={departmentQuery}
      notFoundProps={{
        title: t("masterdata:DepartmentNotFound"),
        message: t("masterdata:DepartmentDoesNotExist"),
        description: t("masterdata:PleaseCheckDepartmentIdOrContactSupport"),
        backTo: "~/masterdata/department",
        backToLabel: t("masterdata:BackToDepartmentOverview"),
      }}
    >
      {departmentQuery.data ? <DepartmentDetailContent department={departmentQuery.data} /> : null}
    </UuidExistenceWrapper>
  );
}

function DepartmentDetailContent({ department }: { department: DepartmentByUuidResponse }): ReactElement {
  const { t } = useTranslation(["masterdata", "common"]);
  const [, navigate] = useLocation();
  const isDeleted = Boolean(department.deletedAt);

  const actions: DetailAction[] = isDeleted
    ? []
    : [
        {
          id: "edit",
          label: t("common:Edit"),
          icon: PencilIcon,
          permission: { action: Action.Manage, subject: "Department" },
          onClick: () => navigate(`~/masterdata/department/${department.uuid}/update`),
        },
      ];

  const tabs: DetailTab[] = [
    {
      id: "users",
      label: t("common:Users"),
      icon: UsersIcon,
      content: () => <UserTable name="department-users" body={{ departments: [department.uuid] }} />,
    },
    {
      id: "identifiers",
      label: t("masterdata:SystemIds"),
      icon: Fingerprint,
      content: () => (
        <SystemIdTab
          uuid={department.uuid}
          createdAt={department.createdAt}
          updatedAt={department.updatedAt}
          deletedAt={department.deletedAt}
          identifiers={[{ id: "v1-id", label: t("masterdata:V1ID"), value: String(department.v1Id) }]}
        />
      ),
    },
  ];

  return (
    <DetailPageLayout
      variant={CenteredLayoutVariant.Wide}
      header={
        <DetailHeader
          icon={<BuildingIcon />}
          title={department.name}
          badges={
            isDeleted ? (
              <Badge variant={BadgeVariant.Outlined} colorVariant={BadgeColorVariant.Error}>
                {t("common:Deleted")}
              </Badge>
            ) : undefined
          }
          actions={actions}
        />
      }
    >
      <DetailTabs sections={tabs} trackingName={DEPARTMENT_DETAIL_TRACKING_NAME} />
    </DetailPageLayout>
  );
}
